import React, { useContext, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { ApplicationContext } from "../../context/GlobalState";
import { t, extractLocaleFromPath } from "../../i18n";
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import HreflangTags from "../../components/HreflangTags/HreflangTags";
import altTextData from "../../data/images/alt-text.json";
import "./Contact.css";

const CALL_GSO_HERO = '/assets/images/heroes/call-georgia-spine.webp';

export default function Contact() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const { locale } = extractLocaleFromPath(location.pathname);

  const handleSubmit = (e) => { e.preventDefault(); setSubmitted(true); };

  const brand = state.brand || state.client;
  const baseDomain = brand?.domain || state.client?.domain || 'gaspineortho.com';
  const brandKey = brand?.brandKey || 'gso';
  const physicalSites = state.brand?.physicalSites || [];
  const offices = state.brand?.offices || [];
  const phoneNew = brand?.phone || state.client.phone;
  const phoneExisting = brand?.phoneExisting;

  const isGso = brandKey === 'gso';
  const contactHeroBg = isGso ? CALL_GSO_HERO : null;
  const contactHeroAlt = isGso
    ? (altTextData['/assets/images/heroes/call-georgia-spine.webp']?.[locale] || altTextData['/assets/images/heroes/call-georgia-spine.webp']?.en || '')
    : '';

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: `Contact ${state.client.name}`,
    url: `https://${baseDomain}/contact`,
    telephone: state.client.phone,
    email: state.client.email,
    address: offices[0] ? {
      "@type": "PostalAddress",
      streetAddress: offices[0].address
    } : undefined
  };

  return (
    <div className="contact-page">
      <Helmet>
        <title>Contact Us | {state.client.name}</title>
        <meta name="description" content={`Contact ${state.client.name}. Call ${state.client.phone} or send us a message. Free consultations available.`} />
        <meta property="og:title" content={`Contact Us | ${state.client.name}`} />
        <meta property="og:description" content={`Reach out to ${state.client.name}. Free consultations. Call or message us today.`} />
        <meta property="og:url" content={`https://${baseDomain}/contact`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://${baseDomain}/assets/brand/${brandKey}/logo-color.webp`} />
        <link rel="canonical" href={`https://${baseDomain}/contact`} />
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/contact" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(contactSchema) }} />
      <section
        className="contact-hero"
        style={contactHeroBg ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${contactHeroBg})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
        aria-label={contactHeroAlt || undefined}
      ><div className="container"><h1>{t("nav.contact", state.locale)}</h1></div></section>
      <section className="contact-insurance-notice" style={{ background: '#fff5f5', borderTop: '4px solid #c53030', borderBottom: '1px solid #f1c5c5', padding: '1rem 0', textAlign: 'center' }}>
        <div className="container">
          <p style={{ margin: 0, color: '#7a1f1f', fontWeight: 700, fontSize: '1.05rem' }}>
            We do not accept or bill government/commercial health insurance plans.
          </p>
        </div>
      </section>
      <section className="contact-content section-padding">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2>Get in Touch</h2>
              <div className="contact-item"><FaPhone /><div><strong>New Patients</strong><p><a href={`tel:${phoneNew}`}>{phoneNew}</a></p></div></div>
              {phoneExisting && (
                <div className="contact-item"><FaPhone /><div><strong>Existing Patients</strong><p><a href={`tel:${phoneExisting}`}>{phoneExisting}</a></p></div></div>
              )}
              <div className="contact-item"><FaEnvelope /><div><strong>Email</strong><p>{state.client.email}</p></div></div>

              {physicalSites.length > 0 ? (
                <div className="contact-offices">
                  <h3 className="offices-heading"><FaMapMarkerAlt style={{ marginRight: 8 }} />Visit Our Locations</h3>
                  <div className="offices-list">
                    {physicalSites.map((s) => (
                      <div key={s.siteId} className="office-card">
                        <strong>{s.label}</strong>
                        <span style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: 4 }}>{s.type}{s.county ? ` — ${s.county} County` : ''}</span>
                        <p style={{ margin: '0.25rem 0' }}>{s.addr1}<br />{s.city}, {s.state} {s.zip}</p>
                        {s.examRooms ? <p style={{ fontSize: '0.85rem', margin: '0.25rem 0', color: '#555' }}>{s.examRooms} exam rooms{s.openedYear ? ` · opened ${s.openedYear}` : ''}{s.mriOpened ? ` · on-site MRI since ${s.mriOpened}` : ''}</p> : (s.openedYear ? <p style={{ fontSize: '0.85rem', margin: '0.25rem 0', color: '#555' }}>Opened {s.openedYear}</p> : null)}
                        {s.publicNote && <p style={{ fontSize: '0.85rem', margin: '0.25rem 0', color: '#7a1f1f', fontStyle: 'italic' }}>{s.publicNote}</p>}
                        <a href={`tel:${phoneNew}`} className="office-phone">{phoneNew}</a>
                      </div>
                    ))}
                  </div>
                </div>
              ) : offices.length > 0 && (
                <div className="contact-offices">
                  <h3 className="offices-heading"><FaMapMarkerAlt style={{ marginRight: 8 }} />Visit Our Offices</h3>
                  <div className="offices-list">
                    {offices.map((office, i) => (
                      <div key={i} className="office-card">
                        <strong>{office.name}</strong>
                        <p>{office.address}</p>
                        <a href={`tel:${office.phone}`} className="office-phone">{office.phone}</a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="contact-form">
              {submitted ? (
                <div className="form-success"><h3>Thank You!</h3><p>We'll be in touch shortly.</p></div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-group"><label>{t("form.name", state.locale)}</label><input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></div>
                  <div className="form-group"><label>{t("form.email", state.locale)}</label><input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required /></div>
                  <div className="form-group"><label>{t("form.phone", state.locale)}</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required /></div>
                  <div className="form-group"><label>{t("form.message", state.locale)}</label><textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={4} /></div>
                  <button type="submit" className="submit-btn">{t("form.submit", state.locale)}</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
